import { useCallback, useState } from 'react';
import { useReactFlow } from 'reactflow';
import { NodeType } from '@/enums/node-type.enum';
import AddNodeFloatMenu from '../Menu/AddNodeFloatMenu';
import { useFlowView } from '../FlowContext';
import { useSuggestionMenu } from './useSuggestionMenu';
import type { Connection } from '../FlowComponents/FlowTour/ConnectionContext';
import type { AddNewNodeOptions } from '../flowTypes';

interface FloatMenu {
  isOpen: boolean;
  mouseX: number | null;
  mouseY: number | null;
  connection?: Connection | null;
}

interface UseFloatMenuProps {
  addNewNode: ({ action, dropX, dropY, connection }: AddNewNodeOptions) => void;
  openGroupContextMenu: (clientX: number, clientY: number, nodeId: string) => void;
}

export const useFloatMenu = ({ addNewNode, openGroupContextMenu }: UseFloatMenuProps) => {
  const [floatMenu, setFloatMenu] = useState<FloatMenu>({
    mouseX: null,
    mouseY: null,
    isOpen: false,
    connection: null,
  });

  const { screenToFlowPosition } = useReactFlow();
  const flowView = useFlowView();

  const { setAltModifier, anchorEl, isTooltipOpen, onConnectStart, onConnectEnd, onConnect, onEdgeUpdateStart } =
    useSuggestionMenu({
      setFloatMenu,
    });

  const openFloatMenu = useCallback(
    (clientX: number, clientY: number, skipNodeCheck = false) => {
      if (!skipNodeCheck) {
        const flowPosition = screenToFlowPosition({ x: clientX, y: clientY });
        const nodeAtPosition = flowView.findNodeAtPosition(flowPosition);

        if (nodeAtPosition && nodeAtPosition.type === NodeType.CustomGroup) {
          openGroupContextMenu(clientX, clientY, nodeAtPosition.id);
          return;
        }
      }

      setFloatMenu({
        mouseX: clientX - 2,
        mouseY: clientY - 4,
        isOpen: true,
      });
      setAltModifier(false);
    },
    [setAltModifier, screenToFlowPosition, flowView, openGroupContextMenu],
  );

  const handleFloatMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!(event.target instanceof Element) || !event.target.classList.contains('react-flow__pane')) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      openFloatMenu(event.clientX, event.clientY);
    },
    [openFloatMenu],
  );

  const handleFloatMenuClose = useCallback(() => {
    setFloatMenu((current) => ({ ...current, isOpen: false }));
    setAltModifier(false);
  }, [setAltModifier]);

  const floatMenuComponent = floatMenu.isOpen ? (
    <AddNodeFloatMenu
      key="float-menu"
      positionX={floatMenu.mouseX ?? 0}
      positionY={floatMenu.mouseY ?? 0}
      isOpen={floatMenu.isOpen}
      onClose={handleFloatMenuClose}
      addNewNode={addNewNode}
      connection={floatMenu.connection ?? undefined}
    />
  ) : null;

  return {
    floatMenu: {
      menu: floatMenu,
      openFloatMenu,
      handleFloatMenu,
      handleFloatMenuClose,
    },
    floatMenuComponent,
    suggestionMenu: {
      setAltModifier,
      anchorEl,
      isTooltipOpen,
      onConnectStart,
      onConnectEnd,
      onConnect,
      onEdgeUpdateStart,
    },
  };
};
